import { makeAutoObservable, runInAction } from 'mobx';
import { apiClient } from '../api/apiclient';
import { academyStore } from './AcademyStore';
export class UserStore {
    constructor() {
        this.users = [];
        this.loading = false;
        this.error = null;
        this.selectedUser = null;
        this.isDialogOpen = false;
        this.isEditing = false;
        this.currentRole = null; // Track which role table we're adding/editing for
        this.isUploadDialogOpen = false;
        this.uploadDialogUser = null;
        // Dialog actions
        this.openAddDialog = (role) => {
            this.currentRole = role;
            this.selectedUser = null;
            this.isEditing = false;
            this.isDialogOpen = true;
        };
        this.openEditDialog = (user) => {
            this.currentRole = null; // Will be determined by the user's roles
            this.selectedUser = user;
            this.isEditing = true;
            this.isDialogOpen = true;
        };
        this.closeDialog = () => {
            this.isDialogOpen = false;
            this.selectedUser = null;
            this.isEditing = false;
            this.currentRole = null;
        };
        this.openUploadDialog = (user) => {
            this.uploadDialogUser = user;
            this.isUploadDialogOpen = true;
        };
        this.closeUploadDialog = () => {
            this.isUploadDialogOpen = false;
            this.uploadDialogUser = null;
        };
        makeAutoObservable(this);
    }
    // Actions
    async fetchUsers(academyId, filters) {
        this.loading = true;
        this.error = null;
        try {
            const response = await apiClient.getUsersByAcademyId(academyId, filters);
            if (response.success) {
                runInAction(() => {
                    this.users = response.data;
                    this.loading = false;
                });
            }
            else {
                runInAction(() => {
                    this.error = response.message;
                    this.loading = false;
                });
            }
        }
        catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : 'Failed to fetch users';
                this.loading = false;
            });
        }
    }
    async createUser(userData) {
        if (!academyStore.currentAcademy) {
            throw new Error('No academy selected');
        }
        this.loading = true;
        this.error = null;
        try {
            const response = await apiClient.createUser(academyStore.currentAcademy.id, userData);
            if (response.success) {
                runInAction(() => {
                    this.users.unshift(response.data);
                    this.loading = false;
                });
                return response.data;
            }
            else {
                runInAction(() => {
                    this.error = response.message;
                    this.loading = false;
                });
                return null;
            }
        }
        catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : 'Failed to create user';
                this.loading = false;
            });
            throw error;
        }
    }
    async updateUser(id, userData, profilePictureFile) {
        if (!academyStore.currentAcademy) {
            throw new Error('No academy selected');
        }
        this.loading = true;
        this.error = null;
        try {
            const response = await apiClient.updateUser(academyStore.currentAcademy.id, id, userData, profilePictureFile);
            if (response.success) {
                runInAction(() => {
                    const index = this.users.findIndex(user => user.id === id);
                    if (index !== -1) {
                        this.users[index] = response.data;
                    }
                    this.loading = false;
                });
            }
            else {
                runInAction(() => {
                    this.error = response.message;
                    this.loading = false;
                });
            }
        }
        catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : 'Failed to update user';
                this.loading = false;
            });
            throw error;
        }
    }
    async deleteUser(id) {
        if (!academyStore.currentAcademy) {
            throw new Error('No academy selected');
        }
        this.loading = true;
        this.error = null;
        try {
            const response = await apiClient.deleteUser(academyStore.currentAcademy.id, id);
            if (response.success) {
                runInAction(() => {
                    this.users = this.users.filter(user => user.id !== id);
                    this.loading = false;
                });
            }
            else {
                runInAction(() => {
                    this.error = response.message;
                    this.loading = false;
                });
            }
        }
        catch (error) {
            runInAction(() => {
                this.error = error instanceof Error ? error.message : 'Failed to delete user';
                this.loading = false;
            });
            throw error;
        }
    }
    // Computed getters for role-based filtering
    get admins() {
        return this.users.filter(user => user.roles.includes('admin'));
    }
    get coaches() {
        return this.users.filter(user => user.roles.includes('coach'));
    }
    get players() {
        return this.users.filter(user => user.roles.includes('player'));
    }
    // Helper method to get users by role
    getUsersByRole(role) {
        return this.users.filter(user => user.roles.includes(role));
    }
}
export const userStore = new UserStore();
